import { useEffect, useState } from "react"

import { Flex, Modal, Typography } from "antd"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router"

import InputComponent from "../../../../Components/Input"
import SelectComponent from "../../../../Components/Select"
import Icon from "../../../../Icon"
import { debounce } from "../../../../Utils/utils"
import Creators from "../../reducer"
import { useCreditsPageQuery } from "../../useCreditsPageQuery"
import { buildNotifyPayload } from "../../utils/notify"
import { NotifyMerchantCheckbox } from "../NotifyMerchantCheckbox"

const { Text } = Typography

const rejectionReasonOptions = [
  {
    value: "The reason is invalid",
    label: "The reason is invalid"
  },
  {
    value: "No proof provided",
    label: "No proof provided"
  },
  {
    value: "Provided proof is ineligible",
    label: "Provided proof is ineligible"
  },
  {
    value: "Ineligible request",
    label: "Ineligible request"
  },
  {
    value: "Declined by alert provider",
    label: "Declined by alert provider"
  },
  {
    value: "Other",
    label: "Other"
  }
]

export const RejectRequestModal = ({ isOpen, setIsOpen, ids, selectedId }) => {
  const { search } = useLocation()
  const { openCreditID } = useCreditsPageQuery(search)

  const [rejectionReason, setRejectionReason] = useState(null)
  const [details, setDetails] = useState(null)
  const [detailsTouched, setDetailsTouched] = useState(false)
  const [notify, setNotify] = useState(true)

  const dispatch = useDispatch()
  const { fetching, saveFetching, saveResult } = useSelector((state) => state.credits)
  const isMobile = useSelector((state) => state.nav.isMobile)

  const isMulti = ids?.length

  useEffect(() => {
    if (isOpen && saveResult) {
      setIsOpen(null)
      setRejectionReason(null)
      setDetails(null)
      setDetailsTouched(false)
    }
  }, [saveResult])

  useEffect(() => {
    // never carry a suppressed notification over to the next status change
    if (isOpen) {
      setNotify(true)
      return
    }

    if (rejectionReason || details) {
      setRejectionReason(null)
      setDetails(null)
      setDetailsTouched(false)
    }
  }, [isOpen])

  const handleCloseRejectModal = () => {
    setIsOpen(null)
    setRejectionReason(null)
    setDetailsTouched(false)
  }

  const handleChangeReason = (value) => {
    if (value !== "Other" && details) setDetails(null)
    setRejectionReason(value)
  }

  const handleChangeDetails = debounce((e) => {
    const value = e.target.value
    setDetailsTouched(true)
    if (value?.trim()) {
      setDetails(value?.trim())
    } else {
      setDetails(null)
    }
  }, 400)

  const detailsError = detailsTouched && details && details.length < 15
  const isDetailsValid = details && details.length >= 15

  return (
    <Modal
      title={null}
      centered={!isMobile}
      open={isOpen}
      okText={isMulti ? "Change status for all" : "Reject"}
      onOk={() => {
        dispatch(
          Creators.updateCreditRequest({
            ...(isMulti ? { ids: ids?.join(",") } : { id: openCreditID || selectedId }),
            status_type: "request_status",
            status_value: "Rejected",
            rejection_reason: rejectionReason,
            rejection_reason_details: details,
            ...buildNotifyPayload(notify)
          })
        )
      }}
      onCancel={handleCloseRejectModal}
      okButtonProps={{
        size: "large",
        disabled: !rejectionReason || !isDetailsValid,
        loading: fetching || saveFetching
      }}
      cancelButtonProps={{
        size: "large"
      }}
      closable={false}
      maskClosable={false}
      width={442}
    >
      <Flex gap={12} align={"flex-start"} style={{ marginBottom: 40 }}>
        <Icon type={"info_mark_modal"} style={{ fontSize: 40 }} />
        <Flex gap={8} vertical={true}>
          <Text strong>{isMulti ? "Rejected request status" : "Reject credit request"}</Text>
          <Flex gap={24} vertical={true}>
            {isMulti ? (
              <Text type="secondary">
                Are you sure you want to change Request status for all selected requests? Setting a
                new status may affect the credit balance for corresponding business accounts. If
                yes, please specify the new reason below that will be assigned for all requests.
                Please note that for requests with “Approved” and “Auto-approved” statuses no
                further changes can be applied.
              </Text>
            ) : (
              <Text type="secondary">
                Are you sure you want to reject the request so that no credits will be provided for
                the business account? If yes, select the reason and click “Reject” button.
              </Text>
            )}
            <SelectComponent
              size="large"
              name="rejection_reason"
              placeholder="Select rejection reason"
              defaultValue={rejectionReason}
              value={rejectionReason}
              onChange={handleChangeReason}
              options={rejectionReasonOptions}
            />

            <Flex vertical={true} gap={4}>
              <InputComponent
                placeholder={"Add details"}
                onChange={handleChangeDetails}
                status={detailsError ? "error" : undefined}
              />
              {detailsError && (
                <Text type="danger" style={{ fontSize: 12 }}>
                  Must be at least 15 characters
                </Text>
              )}
            </Flex>

            <NotifyMerchantCheckbox checked={notify} onChange={setNotify} />
          </Flex>
        </Flex>
      </Flex>
    </Modal>
  )
}
